import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _inheritsLoose from "@babel/runtime/helpers/esm/inheritsLoose";
import React from 'react';
import PropTypes from 'prop-types';
import throttle from 'lodash.throttle';
import raf from 'raf';
import { isKeyEnter } from "../../../lib/events/keyboard/identifiers";
import { polyfillPlaceholder } from "../../../lib/polyfillPlaceholder";
import * as LayoutEvents from "../../../lib/LayoutEvents";
import { ThemeContext } from "../../../lib/theming/ThemeContext";
import { RenderLayer } from "../../../internal/RenderLayer";
import { ResizeDetector } from "../../../internal/ResizeDetector";
import { isBrowser, isIE11 } from "../../../lib/client";
import { CommonWrapper } from "../../../internal/CommonWrapper";
import { isTestEnv } from "../../../lib/currentEnvironment";
import { cx } from "../../../lib/theming/Emotion";
import { getTextAreaHeight } from "../TextareaHelpers";
import { styles } from "../Textarea.styles";
import { TextareaCounter } from "../TextareaCounter";
var DEFAULT_WIDTH = 250;
var AUTORESIZE_THROTTLE_DEFAULT_WAIT = 100;
/**
 * Компонент для ввода многострочного текста.
 *
 * Принимает все атрибуты `React.TextareaHTMLAttributes<HTMLTextAreaElement>`
 *
 * ** `className` и `style`  игнорируются**
 */

export var Textarea = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(Textarea, _React$Component);

  function Textarea() {
    var _this;

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _React$Component.call.apply(_React$Component, [this].concat(args)) || this;
    _this.state = {
      polyfillPlaceholder: polyfillPlaceholder,
      rows: 1,
      isCounterVisible: false
    };

    _this.reflowCounter = function () {
      if (_this.counter) {
        _this.counter.reflow();
      }
    };

    _this.theme = void 0;
    _this.selectAllId = null;
    _this.node = void 0;
    _this.fakeNode = void 0;
    _this.counter = void 0;
    _this.layoutEvents = void 0;
    _this.textareaObserver = isBrowser ? new MutationObserver(_this.reflowCounter) : null;

    _this.setSelectionRange = function (start, end) {
      if (!_this.node) {
        throw new Error('Cannot call "setSelectionRange" on unmounted Input');
      }

      if (document.activeElement !== _this.node) {
        _this.focus();
      }

      _this.node.setSelectionRange(start, end);
    };

    _this.selectAll = function () {
      if (_this.node) {
        _this.setSelectionRange(0, _this.node.value.length);
      }
    };

    _this.delaySelectAll = function () {
      return _this.selectAllId = raf(_this.selectAll);
    };

    _this.cancelDelayedSelectAll = function () {
      if (_this.selectAllId) {
        raf.cancel(_this.selectAllId);
        _this.selectAllId = null;
      }
    };

    _this.renderMain = function (props) {
      var _cx, _ref, _textareaProps$maxLen;

      var _props$width = props.width,
          width = _props$width === void 0 ? DEFAULT_WIDTH : _props$width,
          error = props.error,
          warning = props.warning,
          autoResize = props.autoResize,
          resize = props.resize,
          onCut = props.onCut,
          onPaste = props.onPaste,
          maxRows = props.maxRows,
          onFocus = props.onFocus,
          selectAllOnFocus = props.selectAllOnFocus,
          placeholder = props.placeholder,
          onValueChange = props.onValueChange,
          showLengthCounter = props.showLengthCounter,
          lengthCounter = props.lengthCounter,
          counterHelp = props.counterHelp,
          extraRow = props.extraRow,
          disableAnimations = props.disableAnimations,
          textareaProps = _objectWithoutPropertiesLoose(props, ["width", "error", "warning", "autoResize", "resize", "onCut", "onPaste", "maxRows", "onFocus", "selectAllOnFocus", "placeholder", "onValueChange", "showLengthCounter", "lengthCounter", "counterHelp", "extraRow", "disableAnimations"]);

      var isCounterVisible = _this.state.isCounterVisible;
      var rootProps = {
        style: {
          width: width
        }
      };
      var textareaClassNames = cx((_cx = {}, _cx[styles.textarea(_this.theme)] = true, _cx[styles.error(_this.theme)] = !!error, _cx[styles.warning(_this.theme)] = !!warning, _cx[styles.disableAnimations()] = _this.isAnimationsDisabled(), _cx));
      var textareaStyle = {
        resize: autoResize ? 'none' : resize
      };
      var placeholderPolyfill = null;

      if (_this.state.polyfillPlaceholder && !textareaProps.value) {
        placeholderPolyfill = /*#__PURE__*/React.createElement("span", {
          className: styles.placeholder()
        }, placeholder);
      }

      var fakeTextarea = null;

      if (autoResize) {
        var fakeProps = {
          value: _this.props.value,
          defaultValue: _this.props.defaultValue,
          className: cx(textareaClassNames, styles.fake()),
          readOnly: true
        };
        fakeTextarea = /*#__PURE__*/React.createElement("textarea", _extends({}, fakeProps, {
          ref: _this.refFake
        }));
      }

      var counter = showLengthCounter && isCounterVisible && _this.node && /*#__PURE__*/React.createElement(TextareaCounter, {
        textarea: _this.node,
        help: counterHelp,
        value: textareaProps.value,
        length: (_ref = (_textareaProps$maxLen = textareaProps.maxLength) != null ? _textareaProps$maxLen : lengthCounter) != null ? _ref : 0,
        onCloseHelp: _this.handleCloseCounterHelp,
        ref: _this.refCounter
      });
      return /*#__PURE__*/React.createElement(RenderLayer, {
        onFocusOutside: _this.handleCloseCounterHelp,
        onClickOutside: _this.handleCloseCounterHelp,
        active: _this.state.isCounterVisible
      }, /*#__PURE__*/React.createElement("label", _extends({}, rootProps, {
        className: styles.root(_this.theme)
      }), placeholderPolyfill, /*#__PURE__*/React.createElement(ResizeDetector, {
        onResize: _this.reflowCounter
      }, /*#__PURE__*/React.createElement("textarea", _extends({}, textareaProps, {
        className: textareaClassNames,
        style: textareaStyle,
        placeholder: !placeholderPolyfill ? placeholder : undefined,
        ref: _this.ref,
        onChange: _this.handleChange,
        onCut: _this.handleCut,
        onPaste: _this.handlePaste,
        onFocus: _this.handleFocus,
        onKeyDown: _this.handleKeyDown
      }), _this.props.children)), fakeTextarea, counter));
    };

    _this.handleCloseCounterHelp = function () {
      return _this.setState({
        isCounterVisible: false
      });
    };

    _this.handleKeyDown = function (e) {
      // Edge bug: textarea maxlength doesn't work after new line
      // https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/18833616/
      var value = _this.props.value !== undefined ? _this.props.value.toString() : null;

      var isBlockEnter = isKeyEnter(e) && value !== null && value.length === _this.props.maxLength;

      if (isBlockEnter) {
        e.preventDefault();
      }

      if (_this.props.onKeyDown) {
        _this.props.onKeyDown(e);
      }
    };

    _this.handleChange = function (e) {
      if (polyfillPlaceholder) {
        var fieldIsEmpty = e.target.value === '';

        if (_this.state.polyfillPlaceholder !== fieldIsEmpty) {
          _this.setState({
            polyfillPlaceholder: fieldIsEmpty
          });
        }
      }

      if (_this.props.onValueChange) {
        _this.props.onValueChange(e.target.value);
      }

      if (_this.props.autoResize) {
        _this.autoResize();
      }

      if (_this.props.onChange) {
        _this.props.onChange(e);
      }

      _this.reflowCounter();
    };

    _this.ref = function (element) {
      _this.node = element;
    };

    _this.refFake = function (element) {
      _this.fakeNode = element;
    };

    _this.refCounter = function (ref) {
      _this.counter = ref;
    };

    _this.autoResizeHandler = function () {
      var fakeNode = _this.fakeNode;

      if (!fakeNode) {
        return;
      }

      var node = _this.node;

      if (!node) {
        return;
      }

      if (_this.props.value === undefined) {
        fakeNode.value = node.value;
      }

      var _this$props = _this.props,
          rows = _this$props.rows,
          maxRows = _this$props.maxRows,
          extraRow = _this$props.extraRow;

      if (rows === undefined || maxRows === undefined) {
        return;
      }

      var _getTextAreaHeight = getTextAreaHeight({
        node: fakeNode,
        minRows: typeof rows === 'number' ? rows : parseInt(rows, 10),
        maxRows: typeof maxRows === 'number' ? maxRows : parseInt(maxRows, 10),
        extraRow: extraRow
      }),
          height = _getTextAreaHeight.height,
          exceededMaxHeight = _getTextAreaHeight.exceededMaxHeight;

      node.style.height = height + 'px';
      node.style.overflowY = exceededMaxHeight ? 'scroll' : 'hidden';
      fakeNode.style.overflowY = exceededMaxHeight ? 'scroll' : 'hidden';
    };

    _this.autoResize = throttle(_this.autoResizeHandler, _this.getAutoResizeThrottleWait());

    _this.handleCut = function (event) {
      if (_this.props.autoResize) {
        _this.autoResize();
      }

      if (_this.props.onCut) {
        _this.props.onCut(event);
      }

      _this.reflowCounter();
    };

    _this.handlePaste = function (event) {
      if (_this.props.autoResize) {
        _this.autoResize();
      }

      if (_this.props.onPaste) {
        _this.props.onPaste(event);
      }

      _this.reflowCounter();
    };

    _this.handleFocus = function (event) {
      _this.setState({
        isCounterVisible: true
      });

      if (_this.props.selectAllOnFocus) {
        // https://github.com/facebook/react/issues/7769
        _this.node && !isIE11 ? _this.selectAll() : _this.delaySelectAll();
      }

      if (_this.props.onFocus) {
        _this.props.onFocus(event);
      }
    };

    return _this;
  }

  var _proto = Textarea.prototype;

  _proto.getAutoResizeThrottleWait = function getAutoResizeThrottleWait(props) {
    if (props === void 0) {
      props = this.props;
    } // NOTE: При отключении анимации остается эффект дергания при авто-ресайзе из-за троттлинга расчета высоты
    // Поэтому выставляем таймаут троттла в ноль. Подробности - https://github.com/skbkontur/retail-ui/issues/2120


    return this.isAnimationsDisabled(props) ? 0 : AUTORESIZE_THROTTLE_DEFAULT_WAIT;
  };

  _proto.isAnimationsDisabled = function isAnimationsDisabled(_temp) {
    var _ref2 = _temp === void 0 ? this.props : _temp,
        disableAnimations = _ref2.disableAnimations,
        extraRow = _ref2.extraRow;

    return disableAnimations || !extraRow;
  };

  _proto.componentDidMount = function componentDidMount() {
    if (this.props.autoResize) {
      this.autoResize();
      this.layoutEvents = LayoutEvents.addListener(this.autoResize);
    }

    if (this.node && this.props.showLengthCounter && this.textareaObserver) {
      this.textareaObserver.observe(this.node, {
        attributes: true
      });
    }
  };

  _proto.componentWillUnmount = function componentWillUnmount() {
    if (this.layoutEvents) {
      this.layoutEvents.remove();
    }

    if (this.props.showLengthCounter && this.textareaObserver) {
      this.textareaObserver.disconnect();
    }

    this.cancelDelayedSelectAll();
  };

  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    if (this.getAutoResizeThrottleWait() !== this.getAutoResizeThrottleWait(prevProps)) {
      this.autoResize.cancel();
      this.autoResize = throttle(this.autoResizeHandler, this.getAutoResizeThrottleWait());
    }

    if (this.props.autoResize && this.props.rows > this.state.rows || this.props.value !== prevProps.value) {
      this.autoResize();
    }
  };

  _proto.render = function render() {
    var _this2 = this;

    return /*#__PURE__*/React.createElement(ThemeContext.Consumer, null, function (theme) {
      _this2.theme = theme;
      return /*#__PURE__*/React.createElement(CommonWrapper, _this2.props, _this2.renderMain);
    });
  }
  /**
  * @public
  */
  ;

  _proto.focus = function focus() {
    if (this.node) {
      this.node.focus();
    }
  }
  /**
  * @public
  */
  ;

  _proto.blur = function blur() {
    if (this.node) {
      this.node.blur();
    }
  }
  /**
  * @public
  * @param {number} start
  * @param {number} end
  */
  ;

  return Textarea;
}(React.Component);
Textarea.__KONTUR_REACT_UI__ = 'Textarea';
Textarea.propTypes = {
  error: PropTypes.bool,
  warning: PropTypes.bool,
  disabled: PropTypes.bool,
  autoResize: PropTypes.bool,
  extraRow: PropTypes.bool,
  disableAnimations: PropTypes.bool,
  maxRows: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  resize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  id: PropTypes.string,
  name: PropTypes.string,
  title: PropTypes.string,
  spellCheck: PropTypes.bool,
  role: PropTypes.string,
  maxLength: PropTypes.number,
  tabIndex: PropTypes.number,
  rows: PropTypes.number,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  defaultValue: PropTypes.string,
  onValueChange: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onMouseOver: PropTypes.func,
  onMouseMove: PropTypes.func,
  onMouseOut: PropTypes.func,
  onMouseUp: PropTypes.func,
  onMouseDown: PropTypes.func,
  onClick: PropTypes.func,
  onDoubleClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyPress: PropTypes.func,
  onKeyUp: PropTypes.func,
  onInput: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onScroll: PropTypes.func,
  onWheel: PropTypes.func,
  onCut: PropTypes.func,
  onPaste: PropTypes.func,
  onCopy: PropTypes.func
};
Textarea.defaultProps = {
  rows: 3,
  maxRows: 15,
  extraRow: true,
  disableAnimations: isTestEnv
};